// „За внимание" — сигналите, за които има ЗАПИСАНИ данни, не догадки.
//
// Два вида: дълго отворена маса и едра отстъпка. Клик отваря историята на
// операциите по сметката. Хедърът е само заглавието — броячът и подсказката
// за клика не носят информация, която редовете вече да не показват; изречение
// има само празната карта, иначе тя мълчи.
//
// Признакът „закрита без номер на бон" НЕ е сигнал тук: сигналът е изречение,
// а той се показва без думи — с точка с контур на реда в „История на маси".

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import { formatMinutes, parseDecimal } from "../../../../components/dataTable";
import type {
  DashboardAlert,
  DashboardAlertKind,
} from "../../../../api/reportsDashboard";

/** Тонът на точката пред сигнала. */
const TONE: Record<DashboardAlertKind, string> = {
  big_discount: "bg-brand-deep",
  open_long: "bg-brand",
};

type Props = {
  alerts: DashboardAlert[];
  onOpenBill: (billId: number) => void;
};

// БЕЗ бележка „влезли сте без втори код": тя сама издава, че съществува
// скрито съдържание. Ограниченият изглед просто НЕ показва скритото — не го
// обявява. Признакът за приета сума без документ пък се показва без думи и само
// когато сесията Е с втори код.
export const DashboardAlertsCard = ({ alerts, onOpenBill }: Props) => {
  const { t } = useTranslation();

  const titleOf = (alert: DashboardAlert) => {
    const value = parseDecimal(alert.value);
    if (alert.kind === "open_long") {
      return t("reportsPage.dashAlertOpenLong", {
        table: alert.table_name,
        time: formatMinutes(value, {
          hour: t("dataTable.hourShort"),
          minute: t("dataTable.minuteShort"),
        }),
      });
    }
    return t("reportsPage.dashAlertBigDiscount", {
      table: alert.table_name,
      percent: Math.round(value),
    });
  };

  return (
    <section className="flex flex-col rounded-xl border border-shade-2 bg-surface px-4 py-3.5">
      <h2 className="mb-3 font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.dashAlertsTitle")}
      </h2>

      {alerts.length ? (
        <ul className="flex flex-col gap-1.5">
          {alerts.map((alert) => (
            <li key={`${alert.kind}-${alert.bill_id}`}>
              <button
                type="button"
                onClick={() => onOpenBill(alert.bill_id)}
                className="flex w-full items-center gap-2.5 rounded-lg border border-shade-2 px-3 py-2 text-left transition-colors hover:bg-fill"
              >
                <span
                  aria-hidden
                  className={`size-[9px] shrink-0 rounded-full ${TONE[alert.kind]}`}
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[13px] font-semibold text-ink">
                    {titleOf(alert)}
                  </span>
                  <span className="truncate text-[11.5px] font-medium text-muted">
                    {alert.waiter_name}
                  </span>
                </span>
                <span className="shrink-0 font-pos-mono text-[13px] font-bold tabular-nums text-ink">
                  {fmtEuro(parseDecimal(alert.amount))}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12px] font-semibold text-soft">
          {t("reportsPage.dashAlertsNone")}
        </p>
      )}
    </section>
  );
};
